import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import type { Cart, CartItem } from '@/types/cart';
import type { Drone } from '@/types/drones';

const CART_STORAGE_KEY = 'fsd-cart';

interface CartContextValue {
  cart: Cart;
  items: CartItem[];
  totalItems: number;
  subtotal: number;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  addItem: (drone: Drone, quantity?: number, isUsed?: boolean) => void;
  removeItem: (itemId: string, isUsed: boolean) => void;
  updateQuantity: (itemId: string, isUsed: boolean, quantity: number) => void;
  clearCart: () => void;
}

const CartContext = createContext<CartContextValue | undefined>(undefined);

function loadCartFromStorage(): Cart {
  try {
    const stored = localStorage.getItem(CART_STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.error('Failed to load cart from storage:', error);
  }
  return {
    items: [],
    createdAt: Date.now(),
    updatedAt: Date.now(),
  };
}

function saveCartToStorage(cart: Cart): void {
  try {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cart));
  } catch (error) {
    console.error('Failed to save cart to storage:', error);
  }
}

export function CartProvider({ children }: { children: React.ReactNode }) {
  const [cart, setCart] = useState<Cart>(loadCartFromStorage);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    saveCartToStorage(cart);
  }, [cart]);

  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === CART_STORAGE_KEY && e.newValue) {
        try {
          const newCart = JSON.parse(e.newValue);
          setCart(newCart);
        } catch (error) {
          console.error('Failed to parse cart from storage event:', error);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const addItem = useCallback((drone: Drone, quantity = 1, isUsed = false) => {
    setCart((prevCart) => {
      const existingItem = prevCart.items.find(
        (item) => item.id === drone.id && item.isUsed === isUsed
      );

      if (existingItem) {
        return {
          ...prevCart,
          items: prevCart.items.map((item) =>
            item.id === drone.id && item.isUsed === isUsed
              ? { ...item, quantity: item.quantity + quantity }
              : item
          ),
          updatedAt: Date.now(),
        };
      }

      return {
        ...prevCart,
        items: [
          ...prevCart.items,
          {
            id: drone.id,
            drone,
            quantity,
            isUsed,
          },
        ],
        updatedAt: Date.now(),
      };
    });
  }, []);

  const removeItem = useCallback((itemId: string, isUsed: boolean) => {
    setCart((prevCart) => ({
      ...prevCart,
      items: prevCart.items.filter((item) => !(item.id === itemId && item.isUsed === isUsed)),
      updatedAt: Date.now(),
    }));
  }, []);

  const updateQuantity = useCallback(
    (itemId: string, isUsed: boolean, quantity: number) => {
      if (quantity <= 0) {
        removeItem(itemId, isUsed);
        return;
      }

      setCart((prevCart) => ({
        ...prevCart,
        items: prevCart.items.map((item) =>
          item.id === itemId && item.isUsed === isUsed ? { ...item, quantity } : item
        ),
        updatedAt: Date.now(),
      }));
    },
    [removeItem]
  );

  const clearCart = useCallback(() => {
    setCart({
      items: [],
      createdAt: Date.now(),
      updatedAt: Date.now(),
    });
  }, []);

  const totalItems = cart.items.reduce((sum, item) => sum + item.quantity, 0);
  const subtotal = 0; // Preços removidos - entre em contato para valores

  const value: CartContextValue = {
    cart,
    items: cart.items,
    totalItems,
    subtotal,
    isOpen,
    setIsOpen,
    addItem,
    removeItem,
    updateQuantity,
    clearCart,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}
